import { Address, ToolkitListingID, addressToString, isDefined } from '@aloft/toolkit-shared';

import { Coordinates } from '../components/map/types.ts';
import { useGetListing } from '../controller/hooks/listings/useGetListing.ts';
import { useLatLngFromAddress } from './googleMaps/useLatLngFromAddress.ts';

/** Gets the coordinates for a property from Google Maps and falls back to MLS data */
export const usePropertyCoordinates = ({
  address,
  listingID,
  alreadyHaveCoords = false,
}: {
  address?: Address;
  listingID?: ToolkitListingID;
  alreadyHaveCoords?: boolean;
}): {
  isLoading: boolean;
  coordinates?: Coordinates;
} => {
  // don't try to get the listing data if we already have coords
  const listingReq = useGetListing(listingID, alreadyHaveCoords);

  // don't geocode if we are still loading the listing, or if we have loaded
  // the listing successfully and have lat and lng. we only want to make the
  // call to google if we have to.
  const alreadyHaveLatLng =
    alreadyHaveCoords ||
    (listingReq.status === 'success' &&
      listingReq.data?.status === 'found' &&
      isDefined(listingReq.data?.listing?.Latitude) &&
      isDefined(listingReq.data?.listing?.Longitude));
  const shouldGeocode = !listingReq.isLoading && !alreadyHaveLatLng;

  const { latLng: googleMapCoords, status: googleMapStatus } = useLatLngFromAddress(
    addressToString(address ?? {}),
    shouldGeocode,
  );

  if (googleMapStatus === 'pending' || listingReq.status === 'loading') {
    return { isLoading: true, coordinates: undefined };
  }

  if (isDefined(googleMapCoords)) {
    return { isLoading: false, coordinates: googleMapCoords };
  }

  if (listingReq.status !== 'success' || listingReq.data?.status !== 'found') {
    return { isLoading: true, coordinates: undefined };
  }

  const { listing } = listingReq.data;
  if (isDefined(listing.Latitude) && isDefined(listing.Longitude)) {
    return { isLoading: false, coordinates: { lat: listing.Latitude, lng: listing.Longitude } };
  }

  return { isLoading: false, coordinates: undefined };
};
